"use client"

import { cn } from "@/lib/utils"

/**
 * A total broken into its parts — one stacked bar and a legend under it, from
 * canvas 7c's "Revenue by company".
 *
 * A stacked bar rather than a pie: the parts are read against each other and
 * against the whole, which is what a length does and an angle does not. The
 * legend carries the numbers, so the bar never has to.
 */

export type Share = {
  /** Stable across renders — the legend and the segment are keyed by it. */
  id: string
  label: string
  /** Already formatted: "$32,000". */
  amount: string
  /** 0–1 of the total. */
  share: number
  /** A CSS colour, usually from `seriesHex(rank)`. */
  color: string
}

/** The smallest a segment may be drawn, so a tiny share does not read as none. */
const MIN_SEGMENT = 1.5
/** 7c separates the segments by two pixels. */
const GAP = 2

/**
 * Widths that honour that floor and still sum to exactly 100.
 *
 * Flooring each segment on its own overflows the bar once enough small ones are
 * in it, and flex then shrinks everything — so every share ends up drawn wrong
 * to make room for the smallest. Raising the floor and renormalising keeps the
 * bar exact and the distortion confined to the slivers that needed it.
 */
function widths(shares: Share[]) {
  const floored = shares.map((share) => Math.max(share.share * 100, MIN_SEGMENT))
  const total = floored.reduce((sum, width) => sum + width, 0)
  return floored.map((width) => (total > 0 ? (width / total) * 100 : 0))
}

/**
 * Percentages that read as a set: rounded to one place, and the remainder given
 * to the largest share so the column sums to 100 rather than to 99.9.
 */
export function percentages(shares: Share[]) {
  const exact = shares.map((share) => share.share * 100)
  const rounded = exact.map((value) => Math.round(value * 10) / 10)
  const drift = Math.round((100 - rounded.reduce((sum, v) => sum + v, 0)) * 10) / 10

  if (drift !== 0 && rounded.length > 0) {
    let largest = 0
    for (let index = 1; index < exact.length; index += 1) {
      if (exact[index] > exact[largest]) largest = index
    }
    rounded[largest] = Math.round((rounded[largest] + drift) * 10) / 10
  }
  return rounded
}

export function ShareBar({
  shares,
  height = 18,
  className,
}: {
  shares: Share[]
  height?: number
  className?: string
}) {
  if (shares.length === 0) return null

  const drawn = widths(shares)
  const shown = percentages(shares)
  /*
    The gutters take real pixels, so percentages that sum to 100 sum to more
    than the bar once the gaps are added and the last segment is clipped. Each
    segment gives back its share of the total gutter width.
  */
  const gutter = (GAP * (shares.length - 1)) / shares.length

  return (
    <div
      className={cn("flex overflow-hidden", className)}
      style={{ height, gap: GAP }}
      role="img"
      aria-label={shares
        .map((share, index) => `${share.label} ${shown[index]}%`)
        .join(", ")}
    >
      {shares.map((share, index) => (
        <span
          key={share.id}
          aria-hidden
          className={cn(
            "flex-none",
            index === 0 && "rounded-l-[4px]",
            index === shares.length - 1 && "rounded-r-[4px]",
          )}
          style={{
            width: `calc(${drawn[index]}% - ${gutter}px)`,
            backgroundColor: share.color,
            height,
          }}
        />
      ))}
    </div>
  )
}

export function ShareLegend({
  shares,
  className,
}: {
  shares: Share[]
  className?: string
}) {
  // The same figures the bar announces, so the two never disagree by a tenth.
  const shown = percentages(shares)

  return (
    <div className={className}>
      {shares.map((share, index) => (
        <div
          key={share.id}
          className={cn(
            "flex items-center gap-2.5 py-2.5",
            index > 0 && "border-t",
          )}
        >
          <span
            aria-hidden
            className="size-[9px] flex-none rounded-[2px]"
            style={{ backgroundColor: share.color }}
          />
          <span className="min-w-0 flex-1 truncate text-[12.5px] leading-[1.3]">
            {share.label}
          </span>
          <span className="whitespace-nowrap font-mono text-[12.5px] font-medium">
            {share.amount}
          </span>
          <span className="w-11 flex-none text-right font-mono text-[11.5px] text-muted-foreground">
            {shown[index].toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  )
}
